import React from 'react';
import { Brain, Clock, Shield, Sparkles, MessageCircle, Globe, Cpu } from 'lucide-react';
import { motion } from 'framer-motion';
import { FeatureCard } from '../ui/FeatureCard';

const features = [
  {
    icon: Brain,
    title: 'Geavanceerd Taalbegrip',
    description: 'Onze AI verwerkt complexe vragen met contextueel begrip, sentimentanalyse en intentieherkenning voor nauwkeurigere en relevantere antwoorden.'
  },
  {
    icon: MessageCircle,
    title: 'Multichannel Ondersteuning',
    description: 'Integreer AI-chat naadloos op websites, mobiele apps en berichtplatforms met behoud van een consistente gesprekshistorie.'
  },
  {
    icon: Globe,
    title: 'Meertalige Communicatie',
    description: 'Automatische taalherkenning en realtime vertaling maken natuurlijke gesprekken in meerdere talen mogelijk, waardoor u wereldwijd bereikbaar bent.'
  },
  {
    icon: Cpu,
    title: 'Zelflerende Technologie',
    description: 'Onze AI leert continu van interacties, verbetert de nauwkeurigheid van antwoorden en personaliseert gesprekken op basis van gebruikerservaringen.'
  },
  {
    icon: Clock,
    title: '24/7 Beschikbaarheid',
    description: 'Bied directe ondersteuning op elk moment van de dag, zonder wachttijden en met de mogelijkheid om meerdere gesprekken tegelijk te voeren.'
  },
  {
    icon: Shield,
    title: 'Bedrijfswaardige Beveiliging',
    description: 'Bankwaardige versleuteling, naleving van privacywetgeving en veilige gespreksafhandeling voor optimale gegevensbescherming.'
  }
];

export function FeaturesSection() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-brand-50 text-brand-600 px-4 py-1.5 rounded-full font-medium mb-6"
          >
            <Sparkles size={20} />
            Innovatieve AI-Technologie
          </motion.div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Intelligente Gesprekken op Schaal
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Onze AI-chatassistent combineert geavanceerde natuurlijke taalverwerking, machine learning en contextueel begrip om menselijke gesprekken te voeren die de klanttevredenheid en bedrijfsefficiëntie verhogen.
          </p>
          <div className="mt-6 text-gray-600 max-w-3xl mx-auto">
            <p>
              Dankzij geavanceerde GPT-technologie begrijpt onze AI complexe vragen, behoudt het de context tijdens gesprekken en leert het van elke interactie om steeds persoonlijkere antwoorden te geven. Met realtime taalherkenning en vertaalmogelijkheden kunt u klanten wereldwijd in hun eigen taal bedienen.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <FeatureCard
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 bg-brand-50 rounded-2xl p-8 text-center"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Transformatieve AI-Technologie
          </h3>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Onze AI-chatoplossing maakt gebruik van de nieuwste ontwikkelingen in machine learning en natuurlijke taalverwerking om context te begrijpen, gespreksverloop te behouden en accurate, behulpzame antwoorden te geven. Met continue leermogelijkheden wordt het systeem steeds intelligenter en persoonlijker, zich aanpassend aan uw bedrijfsbehoeften en klantvoorkeuren.
          </p>
        </motion.div>
      </div>
    </section>
  );
}